import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ImagePlus, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { apiService } from '@/services/api';
import { enviarImagem } from '../manual-api';
import { useManualAtual } from '../contexto-manual';

const LADO_MAXIMO = 1400;

/** Redimensiona e recomprime a imagem antes de guardá-la no rascunho. */
export async function prepararImagem(arquivo: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolver, rejeitar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result));
    leitor.onerror = () => rejeitar(new Error('Falha ao ler o arquivo'));
    leitor.readAsDataURL(arquivo);
  });

  try {
    const imagem = await new Promise<HTMLImageElement>((resolver, rejeitar) => {
      const elemento = new Image();
      elemento.onload = () => resolver(elemento);
      elemento.onerror = () => rejeitar(new Error('Formato não suportado'));
      elemento.src = dataUrl;
    });

    const escala = Math.min(1, LADO_MAXIMO / Math.max(imagem.width, imagem.height));
    if (escala === 1 && dataUrl.length < 600_000) return dataUrl;

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(imagem.width * escala);
    canvas.height = Math.round(imagem.height * escala);
    const contexto = canvas.getContext('2d');
    if (!contexto) return dataUrl;
    contexto.fillStyle = '#ffffff';
    contexto.fillRect(0, 0, canvas.width, canvas.height);
    contexto.drawImage(imagem, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.82);
  } catch {
    return dataUrl;
  }
}

/** Uma imagem já hospedada pelo servidor deste manual, e não um base64. */
function ehArquivoDoServidor(imagem: string | null): boolean {
  return Boolean(imagem && imagem.startsWith('/uploads/manuais/'));
}

export function SlotImagem({ rotulo, descricao, imagem, altura = 180, aoDefinir, conteudo }: {
  rotulo: string;
  descricao?: string;
  imagem: string | null;
  altura?: number;
  aoDefinir: (imagem: string | null) => void;
  /**
   * O que mostrar no lugar da miniatura quando já há imagem (ex.: a figura com
   * pontos clicáveis). Aí o clique na área deixa de abrir o seletor de arquivo;
   * trocar a foto passa a ser pelo botão "Trocar".
   */
  conteudo?: ReactNode;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [carregando, setCarregando] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);
  const { manualId } = useManualAtual();

  const selecionar = async (arquivo?: File) => {
    if (!arquivo) return;
    setCarregando(true);
    setFalha(null);
    try {
      // Com manual gravado, a foto vira arquivo no servidor e o documento
      // guarda só a URL. Sem manual (rascunho local), não há para onde subir:
      // cai no base64, que é o que a cota do navegador aguenta.
      if (manualId) {
        try {
          aoDefinir(await enviarImagem(manualId, arquivo));
          return;
        } catch (erro) {
          setFalha(erro instanceof Error ? erro.message : 'Falha ao enviar; a imagem ficou só neste navegador.');
        }
      }
      aoDefinir(await prepararImagem(arquivo));
    } finally {
      setCarregando(false);
      if (entrada.current) entrada.current.value = '';
    }
  };

  const remover = () => {
    // Apagar o arquivo é acessório: se falhar, o documento já não o referencia.
    if (manualId && ehArquivoDoServidor(imagem)) {
      void apiService.removerImagemManual(manualId, imagem as string).catch(() => undefined);
    }
    setFalha(null);
    aoDefinir(null);
  };

  return (
    <div className="manual-slot-imagem">
      <div className="manual-slot-topo">
        <strong>{rotulo}</strong>
        {imagem && (
          <span className="manual-slot-acoes">
            {conteudo && (
              <button type="button" className="manual-slot-trocar" onClick={() => entrada.current?.click()}>
                <RefreshCw />Trocar
              </button>
            )}
            <button type="button" className="manual-slot-remover" onClick={remover}>
              <Trash2 />Remover
            </button>
          </span>
        )}
      </div>
      {imagem && conteudo && !carregando ? (
        <div className="manual-slot-area tem-imagem tem-conteudo" style={{ height: altura }}>
          {conteudo}
        </div>
      ) : (
      <button
        type="button"
        className={`manual-slot-area ${imagem ? 'tem-imagem' : ''}`}
        style={{ height: altura }}
        onClick={() => entrada.current?.click()}
        onDragOver={(evento) => evento.preventDefault()}
        onDrop={(evento) => {
          evento.preventDefault();
          void selecionar(evento.dataTransfer.files?.[0]);
        }}
      >
        {carregando ? (
          <span className="manual-slot-vazio"><Loader2 className="gira" />Processando imagem…</span>
        ) : imagem ? (
          <img src={imagem} alt={rotulo} />
        ) : (
          <span className="manual-slot-vazio">
            <ImagePlus />
            <strong>Enviar imagem</strong>
            <small>{descricao ?? 'Clique ou arraste um arquivo. Nada é reaproveitado do manual-modelo.'}</small>
          </span>
        )}
      </button>
      )}
      {falha && <small className="manual-slot-falha">{falha}</small>}
      <input
        ref={entrada}
        type="file"
        accept="image/*"
        hidden
        onChange={(evento) => void selecionar(evento.target.files?.[0])}
      />
    </div>
  );
}
